import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { formatCivilDate, resolveFormattingLocale } from '@eis/core';
import { EmptyState, ErrorState } from '@eis/patterns';
import type { PartnerRoleRule } from '../../policies/model/partners';
import { useLeadVersions } from '../api/leads';
import { changedFields, newestFirst, previousVersion } from '../model/leads';
import type { LeadVersion } from '../model/types';
import { LeadDraftSections } from './LeadDraftSections';
import { LeadStatusChip } from './LeadStatusChip';

/**
 * The History tab (SDD-013 §8.1.3, R-3): one row per recorded version, newest first — when, who,
 * what kind of change, the reason. Selecting a row renders THAT version's body read-only in the
 * overview's own sections, with a marker on every field that differs from the version before.
 *
 * `leads.view` suffices (Q-9): a lead's history is the audit trail of an offer, not a superseded
 * contractual belief. The column is labelled "Stand", never "Version" (SSOT I-6).
 */
export function LeadHistoryTab({
  code,
  rules,
}: {
  readonly code: string;
  readonly rules: readonly PartnerRoleRule[] | undefined;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const locale = resolveFormattingLocale(i18n.language);
  const versions = useLeadVersions(code);
  const [selected, setSelected] = useState<string | null>(null);

  if (versions.isError) {
    return <ErrorState details={versions.error.message} onRetry={() => void versions.refetch()} />;
  }
  if (versions.isPending) return <Skeleton variant="rounded" height={240} />;

  const rows = newestFirst(versions.data.versions);
  const current = rows[0];
  const shown: LeadVersion | undefined = selected === null ? undefined : rows.find((row) => row.version === selected);
  const previous = shown === undefined ? undefined : previousVersion(rows, shown);
  const changed = shown === undefined ? undefined : changedFields(previous?.lead, shown.lead);

  const kindLabel = (row: LeadVersion): string => {
    const kind = row.changeKind ?? '';
    if (kind === 'TRANSITION') {
      return t('leads.history.kinds.TRANSITION', { status: row.lead?.status?.name ?? row.lead?.status?.code ?? '' });
    }
    return t(`leads.history.kinds.${kind}` as never, { defaultValue: kind });
  };

  return (
    <Stack spacing={2.5}>
      <Stack>
        <Typography variant="h3" component="h2">
          {t('leads.history.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('leads.history.description')}
        </Typography>
      </Stack>

      {rows.length === 0 ? (
        <EmptyState description={t('leads.history.empty')} />
      ) : (
        <Table size="small" aria-label={t('leads.history.ariaLabel')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('leads.history.version')}</TableCell>
              <TableCell>{t('leads.history.when')}</TableCell>
              <TableCell>{t('leads.history.who')}</TableCell>
              <TableCell>{t('leads.history.change')}</TableCell>
              <TableCell>{t('leads.list.status')}</TableCell>
              <TableCell>{t('leads.history.reason')}</TableCell>
              <TableCell align="right">{t('leads.history.show')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const isCurrent = row.version === current?.version;
              const isShown = shown?.version === row.version;
              return (
                <TableRow key={row.version} selected={isShown} data-testid="lead-version-row">
                  <TableCell>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <span>{row.version}</span>
                      {isCurrent && <Chip size="small" variant="outlined" label={t('leads.history.current')} />}
                    </Stack>
                  </TableCell>
                  <TableCell>{row.recordedAt === undefined || row.recordedAt === '' ? '—' : formatCivilDate(row.recordedAt.slice(0, 10), locale)}</TableCell>
                  <TableCell>{row.recordedBy ?? '—'}</TableCell>
                  <TableCell>{kindLabel(row)}</TableCell>
                  <TableCell>
                    <LeadStatusChip status={row.lead?.status} />
                  </TableCell>
                  <TableCell>{row.reason === undefined || row.reason === '' ? '—' : row.reason}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant={isShown ? 'contained' : 'text'}
                      onClick={() => {
                        setSelected(isShown ? null : (row.version ?? null));
                      }}
                    >
                      {isShown ? t('leads.history.backToCurrent') : t('leads.history.show')}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {shown?.lead !== undefined && (
        <Stack spacing={2} data-testid="lead-version-view">
          <Alert severity="info" icon={false}>
            {t('leads.history.showing', {
              version: shown.version ?? '',
              date: shown.recordedAt === undefined || shown.recordedAt === '' ? '—' : formatCivilDate(shown.recordedAt.slice(0, 10), locale),
            })}
            {changed?.has('status') === true && (
              <Chip size="small" color="warning" variant="outlined" sx={{ ml: 1 }} label={`${t('leads.list.status')}: ${t('leads.history.changedMarker')}`} />
            )}
          </Alert>
          <LeadDraftSections lead={shown.lead} rules={rules} changed={changed} />
        </Stack>
      )}
    </Stack>
  );
}
